/**
 * Bright, animated, flowing water background — covers the ENTIRE hero
 * with visible horizontal motion at every altitude. Pure CSS + inline SVG.
 */
export default function WaterBackground() {
  return (
    <div
      data-testid="water-background"
      aria-hidden
      className="absolute inset-0 overflow-hidden"
    >
      {/* Base bright sky-to-water gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#EAF8FE] via-[#B7E4F5] to-[#3AA7CE]" />

      {/* Sun glare, top-center */}
      <div className="pointer-events-none absolute -top-32 left-1/2 h-[70vw] w-[70vw] -translate-x-1/2 rounded-full bg-white/70 blur-3xl" />

      {/* Full-hero drifting horizontal light streaks (caustics) — the surface itself moves */}
      <div className="pointer-events-none absolute inset-0 opacity-70">
        <div
          className="animate-wave-slow absolute inset-0 w-[220%]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(105deg, transparent 0 60px, rgba(255,255,255,0.25) 60px 90px, transparent 90px 180px, rgba(255,255,255,0.15) 180px 205px, transparent 205px 320px)",
          }}
        />
        <div
          className="animate-wave-mid absolute inset-0 w-[220%] mix-blend-screen"
          style={{
            backgroundImage:
              "repeating-linear-gradient(78deg, transparent 0 40px, rgba(255,255,255,0.18) 40px 60px, transparent 60px 130px)",
          }}
        />
      </div>

      {/* Layer 1 — HIGH wave crest crossing the middle of the hero */}
      <div className="absolute inset-x-0 top-[26%] h-[60%] overflow-hidden">
        <svg
          className="animate-wave-slow absolute h-full w-[220%]"
          viewBox="0 0 2400 600"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveHigh" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8AD4EE" stopOpacity="0.0" />
              <stop offset="35%" stopColor="#8AD4EE" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#3AA7CE" stopOpacity="0.55" />
            </linearGradient>
          </defs>
          <path
            d="M0,180 C240,80 520,280 780,180 C1040,80 1320,280 1580,180 C1840,80 2120,280 2400,180 L2400,600 L0,600 Z"
            fill="url(#waveHigh)"
          />
        </svg>
      </div>

      {/* Layer 2 — MID wave, medium speed */}
      <div className="absolute inset-x-0 top-[42%] h-[58%] overflow-hidden">
        <svg
          className="animate-wave-mid absolute h-full w-[220%]"
          viewBox="0 0 2400 500"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveMid" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3AA7CE" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#1E7BA6" stopOpacity="0.75" />
            </linearGradient>
          </defs>
          <path
            d="M0,220 C180,120 400,320 620,220 C840,120 1080,320 1320,220 C1560,120 1800,320 2040,220 C2200,180 2320,220 2400,220 L2400,500 L0,500 Z"
            fill="url(#waveMid)"
          />
        </svg>
      </div>

      {/* Layer 3 — LOW deep wave, fastest, richest teal */}
      <div className="absolute inset-x-0 bottom-0 h-[45%] overflow-hidden">
        <svg
          className="animate-wave-fast absolute bottom-0 h-full w-[220%]"
          viewBox="0 0 2400 400"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="waveLow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1A5C82" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#0B3855" stopOpacity="0.85" />
            </linearGradient>
          </defs>
          <path
            d="M0,180 C160,100 360,240 560,180 C760,100 960,240 1160,180 C1360,100 1560,240 1760,180 C1960,100 2160,240 2400,180 L2400,400 L0,400 Z"
            fill="url(#waveLow)"
          />
        </svg>
      </div>

      {/* Foamy crest highlight — very fast, right at the mid-wave */}
      <div className="absolute inset-x-0 top-[38%] h-[6px] overflow-hidden opacity-80">
        <div
          className="animate-wave-fast absolute h-full w-[220%]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, transparent 0 40px, rgba(255,255,255,0.9) 40px 60px, transparent 60px 200px)",
            filter: "blur(1px)",
          }}
        />
      </div>

      {/* Big sunlight caustic pulse */}
      <div
        className="pointer-events-none absolute inset-0 mix-blend-overlay animate-caustic"
        style={{
          background:
            "radial-gradient(ellipse 60% 30% at 30% 30%, rgba(255,255,255,0.6) 0%, transparent 60%)," +
            "radial-gradient(ellipse 50% 25% at 75% 20%, rgba(255,255,255,0.5) 0%, transparent 60%)," +
            "radial-gradient(ellipse 40% 20% at 55% 65%, rgba(255,255,255,0.45) 0%, transparent 60%)",
        }}
      />

      {/* Bobbing surface glints */}
      <div className="animate-bob pointer-events-none absolute left-[8%] top-[52%] h-1 w-24 rounded-full bg-white/80 blur-sm" />
      <div
        className="animate-bob pointer-events-none absolute right-[12%] top-[58%] h-1 w-16 rounded-full bg-white/70 blur-sm"
        style={{ animationDelay: "1.3s" }}
      />
      <div
        className="animate-bob pointer-events-none absolute left-[42%] top-[66%] h-1 w-32 rounded-full bg-white/60 blur-sm"
        style={{ animationDelay: "2.1s" }}
      />
      <div
        className="animate-bob pointer-events-none absolute left-[70%] top-[72%] h-1 w-20 rounded-full bg-white/60 blur-sm"
        style={{ animationDelay: "3.4s" }}
      />

      {/* Fine grain for depth */}
      <div className="grain pointer-events-none absolute inset-0 opacity-25" />
    </div>
  );
}
